/**
 * System Dashboard Utilities
 *
 * Shared formatting and validation helpers for System Dashboard components.
 */

import type {
  TelemetrySnapshot,
  TelemetrySnapshotResponse,
  TelemetryHistoryResponse,
  TelemetryTimeseriesResponse,
  TrafficTotals,
  DailySample,
  VnstatResponse,
  MetricStatus,
  NetworkLiveResponse,
  TelemetryStreamNetMessage,
  TelemetryStreamCpuMessage,
  TelemetryStreamMemMessage,
  NetTimeseriesPoint,
  CpuTimeseriesPoint,
  MemTimeseriesPoint,
  SystemInventoryResponse,
  SystemUsersSummaryResponse,
  SystemUpdatesResponse,
  SystemRedisStatusResponse,
  TelemetryStreamRaidMessage,
  TelemetryStreamHardwareMessage,
  MetricsStoreStats,
  MetricsStoreTier,
  MetricsStatsResponse,
  MetricsPurgeResponse,
} from "./types";

// Type guards
export const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

export const isNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

export const isNumberOrNull = (value: unknown): value is number | null =>
  value === null || isNumber(value);

export const isTuple3 = (value: unknown): value is [number, number, number] =>
  Array.isArray(value) && value.length === 3 && value.every((entry) => isNumber(entry));

export const isTrafficTotals = (value: unknown): value is TrafficTotals =>
  isRecord(value) && isNumber(value.rxBytes) && isNumber(value.txBytes) && isNumber(value.totalBytes);

export const isDailySample = (value: unknown): value is DailySample =>
  isTrafficTotals(value) && typeof (value as DailySample).date === "string";

export const isVnstatResponse = (value: unknown): value is VnstatResponse => {
  if (!isRecord(value) || value.ok !== true) return false;
  if (typeof value.iface !== "string") return false;
  if (value.updatedAt !== null && typeof value.updatedAt !== "string") return false;
  if ("sourceUpdatedAt" in value && value.sourceUpdatedAt !== null && typeof value.sourceUpdatedAt !== "string") {
    return false;
  }
  const totals = [value.today, value.week, value.month, value.last30Days];
  if (!totals.every((entry) => entry === null || isTrafficTotals(entry))) return false;
  if (!Array.isArray(value.dailySeries) || !value.dailySeries.every(isDailySample)) return false;
  if ("meta" in value && value.meta !== undefined) {
    if (!isRecord(value.meta)) return false;
    if (typeof value.meta.tz !== "string") return false;
    if (typeof value.meta.weekStart !== "string") return false;
    if (typeof value.meta.weekStartDay !== "string") return false;
    if (typeof value.meta.monthStart !== "string") return false;
    if (value.meta.seriesStart !== null && typeof value.meta.seriesStart !== "string") return false;
    if (value.meta.seriesEnd !== null && typeof value.meta.seriesEnd !== "string") return false;
  }
  return typeof value.source === "string";
};

export const isTelemetrySnapshotResponse = (value: unknown): value is TelemetrySnapshotResponse => {
  if (!isRecord(value) || value.ok !== true) return false;
  const snapshot = value.snapshot;
  if (!isRecord(snapshot)) return false;
  if (typeof snapshot.sampledAt !== "string") return false;
  const cpu = snapshot.cpu;
  const memory = snapshot.memory;
  const disk = snapshot.disk;
  const network = snapshot.network;
  if (!isRecord(cpu) || !isRecord(memory) || !isRecord(disk) || !isRecord(network)) return false;
  if (!isNumberOrNull(cpu.percent) || !isNumber(cpu.cores) || !isNumber(cpu.threads) || !isTuple3(cpu.loadAvg)) {
    return false;
  }
  if (!isNumber(memory.usedBytes) || !isNumber(memory.totalBytes) || !isNumber(memory.percent)) return false;
  if (typeof disk.mountPath !== "string") return false;
  if (!isNumberOrNull(disk.usedBytes) || !isNumberOrNull(disk.totalBytes) || !isNumberOrNull(disk.percent)) {
    return false;
  }
  if (disk.fsType !== undefined && disk.fsType !== null && typeof disk.fsType !== "string") return false;
  if (network.iface !== null && typeof network.iface !== "string") return false;
  if (!isNumberOrNull(network.rxBytesPerSec) || !isNumberOrNull(network.txBytesPerSec)) return false;
  return true;
};

const isNetPoint = (value: unknown): value is NetTimeseriesPoint =>
  isRecord(value) &&
  isNumber(value.ts) &&
  isNumber(value.rxBps) &&
  isNumber(value.txBps) &&
  isNumber(value.totalBps);

const isCpuPoint = (value: unknown): value is CpuTimeseriesPoint =>
  isRecord(value) &&
  isNumber(value.ts) &&
  isNumberOrNull(value.pct) &&
  (value.load1 === undefined || isNumberOrNull(value.load1)) &&
  (value.load5 === undefined || isNumberOrNull(value.load5)) &&
  (value.load15 === undefined || isNumberOrNull(value.load15)) &&
  (value.user === undefined || isNumberOrNull(value.user)) &&
  (value.system === undefined || isNumberOrNull(value.system)) &&
  (value.iowait === undefined || isNumberOrNull(value.iowait)) &&
  (value.nice === undefined || isNumberOrNull(value.nice)) &&
  (value.irq === undefined || isNumberOrNull(value.irq)) &&
  (value.softirq === undefined || isNumberOrNull(value.softirq)) &&
  (value.steal === undefined || isNumberOrNull(value.steal)) &&
  (value.guest === undefined || isNumberOrNull(value.guest));

const isMemPoint = (value: unknown): value is MemTimeseriesPoint =>
  isRecord(value) &&
  isNumber(value.ts) &&
  isNumber(value.totalBytes) &&
  isNumber(value.freeBytes) &&
  (value.availableBytes === null || value.availableBytes === undefined || isNumber(value.availableBytes)) &&
  isNumber(value.usedBytes) &&
  isNumber(value.usedPct) &&
  isNumber(value.cachedBytes) &&
  isNumber(value.buffersBytes) &&
  isNumber(value.swapTotalBytes) &&
  isNumber(value.swapUsedBytes) &&
  isNumber(value.swapFreeBytes);

export const isTelemetryHistoryResponse = (value: unknown): value is TelemetryHistoryResponse => {
  if (!isRecord(value) || value.ok !== true) return false;
  if (
    value.stream !== "net.throughput" &&
    value.stream !== "cpu.utilization" &&
    value.stream !== "mem.utilization"
  ) return false;
  if (value.schemaVersion !== 2) return false;
  if (value.scope !== undefined && value.scope !== null) {
    if (!isRecord(value.scope)) return false;
    if (value.scope.iface !== undefined && typeof value.scope.iface !== "string") return false;
  }
  if (
    value.range !== "5m" &&
    value.range !== "15m" &&
    value.range !== "1h" &&
    value.range !== "6h" &&
    value.range !== "24h" &&
    value.range !== "7d" &&
    value.range !== "30d" &&
    value.range !== "365d"
  ) return false;
  if (typeof value.updatedAt !== "string") return false;
  if (
    value.source !== "redis" &&
    value.source !== "memory" &&
    value.source !== "disabled" &&
    value.source !== "durable"
  ) return false;
  if (!Array.isArray(value.points)) return false;
  if (value.stream === "net.throughput") return value.points.every(isNetPoint);
  if (value.stream === "cpu.utilization") return value.points.every(isCpuPoint);
  return value.points.every(isMemPoint);
};

export const isTelemetryTimeseriesResponse = (value: unknown): value is TelemetryTimeseriesResponse => {
  if (!isRecord(value) || value.ok !== true) return false;
  if (value.metric !== "net" && value.metric !== "cpu" && value.metric !== "mem") return false;
  if (value.iface !== null && typeof value.iface !== "string") return false;
  if (
    value.range !== "5m" &&
    value.range !== "15m" &&
    value.range !== "1h" &&
    value.range !== "6h" &&
    value.range !== "24h" &&
    value.range !== "7d" &&
    value.range !== "30d" &&
    value.range !== "365d"
  ) return false;
  if (value.resolution !== "raw" && value.resolution !== "1m" && value.resolution !== "1h" && value.resolution !== "1d") return false;
  if (typeof value.updatedAt !== "string") return false;
  if (!Array.isArray(value.points)) return false;
  if (value.metric === "net") return value.points.every(isNetPoint);
  if (value.metric === "cpu") return value.points.every(isCpuPoint);
  return value.points.every(isMemPoint);
};

const isMetricsStoreTier = (value: unknown): value is MetricsStoreTier =>
  isRecord(value) &&
  typeof value.metric === "string" &&
  typeof value.resolution === "string" &&
  isNumber(value.rows) &&
  isNumberOrNull(value.oldestTs) &&
  isNumberOrNull(value.newestTs) &&
  isNumberOrNull(value.retentionMs);

const isRetentionMap = (value: unknown): value is Record<string, number | null> =>
  isRecord(value) && Object.values(value).every(isNumberOrNull);

const isMetricsStoreStats = (value: unknown): value is MetricsStoreStats =>
  isRecord(value) &&
  isNumber(value.totalRows) &&
  isNumberOrNull(value.fileBytes) &&
  isNumberOrNull(value.oldestTs) &&
  isNumberOrNull(value.newestTs) &&
  isRetentionMap(value.retention) &&
  Array.isArray(value.tiers) &&
  value.tiers.every(isMetricsStoreTier);

export const isMetricsStatsResponse = (value: unknown): value is MetricsStatsResponse =>
  isRecord(value) && value.ok === true && isMetricsStoreStats(value.stats);

export const isMetricsPurgeResponse = (value: unknown): value is MetricsPurgeResponse =>
  isRecord(value) && value.ok === true && isNumber(value.deleted) && isMetricsStoreStats(value.stats);

export const isNetworkLiveResponse = (value: unknown): value is NetworkLiveResponse => {
  if (!isRecord(value)) return false;
  if (value.ok === true) {
    return (
      typeof value.iface === "string" &&
      typeof value.sampledAt === "string" &&
      isNumber(value.rxBps) &&
      isNumber(value.txBps) &&
      isNumber(value.totalBps)
    );
  }
  return (
    value.ok === false &&
    typeof value.code === "string" &&
    typeof value.message === "string"
  );
};

export const isTelemetryStreamNetMessage = (value: unknown): value is TelemetryStreamNetMessage => {
  if (!isRecord(value)) return false;
  if (value.ok === true) {
    const sample = value.sample;
    return (
      isRecord(sample) &&
      typeof sample.iface === "string" &&
      typeof sample.sampledAt === "string" &&
      isNumber(sample.rxBps) &&
      isNumber(sample.txBps) &&
      isNumber(sample.totalBps)
    );
  }
  return value.ok === false && typeof value.code === "string" && typeof value.message === "string";
};

export const isTelemetryStreamCpuMessage = (value: unknown): value is TelemetryStreamCpuMessage => {
  if (!isRecord(value)) return false;
  if (value.ok === true) {
    const sample = value.sample;
    if (!isRecord(sample) || typeof sample.sampledAt !== "string" || !isNumberOrNull(sample.pct)) return false;
    if (sample.kind && sample.kind !== "telemetry_sample_v2") return false;
    if (sample.load1 !== undefined && !isNumberOrNull(sample.load1)) return false;
    if (sample.load5 !== undefined && !isNumberOrNull(sample.load5)) return false;
    if (sample.load15 !== undefined && !isNumberOrNull(sample.load15)) return false;
    if (sample.breakdown !== undefined) {
      if (!isRecord(sample.breakdown)) return false;
      const breakdown = sample.breakdown as Record<string, unknown>;
      const fields = ["user", "system", "iowait", "nice", "irq", "softirq", "steal", "guest"] as const;
      if (!fields.every((field) => isNumberOrNull(breakdown[field]))) return false;
    }
    if (sample.topProcesses !== undefined) {
      if (!Array.isArray(sample.topProcesses)) return false;
      const valid = sample.topProcesses.every((entry) =>
        isRecord(entry) &&
        isNumber(entry.pid) &&
        typeof entry.name === "string" &&
        isNumber(entry.cpuPercent) &&
        (entry.memRssBytes === null || entry.memRssBytes === undefined || isNumber(entry.memRssBytes))
      );
      if (!valid) return false;
    }
    return true;
  }
  return value.ok === false && typeof value.message === "string";
};

export const isTelemetryStreamMemMessage = (value: unknown): value is TelemetryStreamMemMessage => {
  if (!isRecord(value)) return false;
  if (value.ok === true) {
    const sample = value.sample;
    if (!isRecord(sample) || typeof sample.sampledAt !== "string" || !isNumber(sample.usedBytes) || !isNumber(sample.usedPct)) {
      return false;
    }
    if (sample.kind && sample.kind !== "telemetry_sample_v2") return false;
    if (sample.totalBytes !== undefined && !isNumber(sample.totalBytes)) return false;
    if (sample.freeBytes !== undefined && !isNumber(sample.freeBytes)) return false;
    if (sample.availableBytes !== undefined && sample.availableBytes !== null && !isNumber(sample.availableBytes)) return false;
    if (sample.cachedBytes !== undefined && !isNumber(sample.cachedBytes)) return false;
    if (sample.buffersBytes !== undefined && !isNumber(sample.buffersBytes)) return false;
    if (sample.swapTotalBytes !== undefined && !isNumber(sample.swapTotalBytes)) return false;
    if (sample.swapUsedBytes !== undefined && !isNumber(sample.swapUsedBytes)) return false;
    if (sample.swapFreeBytes !== undefined && !isNumber(sample.swapFreeBytes)) return false;
    if (sample.swapDevices !== undefined) {
      if (!Array.isArray(sample.swapDevices)) return false;
      const valid = sample.swapDevices.every((entry) =>
        isRecord(entry) &&
        typeof entry.path === "string" &&
        typeof entry.type === "string" &&
        isNumber(entry.sizeBytes) &&
        isNumber(entry.usedBytes) &&
        isNumber(entry.priority)
      );
      if (!valid) return false;
    }
    return true;
  }
  return value.ok === false && typeof value.message === "string";
};

export const isTelemetryStreamRaidMessage = (value: unknown): value is TelemetryStreamRaidMessage => {
  if (!isRecord(value)) return false;
  if (value.ok === true) {
    // Absent signal: { ok: true, absent: true }
    if (value.absent === true) return true;
    // Sample signal: { ok: true, sample: RaidSample }
    const sample = value.sample;
    if (!isRecord(sample)) return false;
    if (typeof sample.sampledAt !== "string") return false;
    if (!Array.isArray(sample.arrays)) return false;
    // Validate first array has required fields (lightweight check)
    if (sample.arrays.length > 0) {
      const first = sample.arrays[0] as unknown;
      if (!isRecord(first)) return false;
      if (typeof first.name !== "string") return false;
      if (typeof first.level !== "string") return false;
      if (typeof first.state !== "string") return false;
    }
    return true;
  }
  return value.ok === false && typeof value.code === "string" && typeof value.message === "string";
};

export const isTelemetryStreamHardwareMessage = (
  value: unknown,
): value is TelemetryStreamHardwareMessage => {
  if (!isRecord(value)) return false;
  if (value.ok === true) {
    const sample = value.sample;
    if (!isRecord(sample)) return false;
    if (typeof sample.sampledAt !== "string") return false;
    // The five required sections distinguish a hardware sample from a RAID one
    // (which carries `arrays` but none of these) — a lightweight shape check.
    if (!isRecord(sample.capabilities)) return false;
    if (!isRecord(sample.platform)) return false;
    if (!isRecord(sample.cpu)) return false;
    if (!isRecord(sample.memory)) return false;
    if (!isRecord(sample.health)) return false;
    return true;
  }
  return value.ok === false && typeof value.code === "string" && typeof value.message === "string";
};

export const isSystemInventoryResponse = (value: unknown): value is SystemInventoryResponse => {
  if (!isRecord(value) || value.ok !== true) return false;
  if (!isRecord(value.inventory)) return false;
  if (!isRecord(value.inventory.os)) return false;
  if (value.inventory.kernel !== null && typeof value.inventory.kernel !== "string") return false;
  if (!isRecord(value.inventory.sqlite3) || !isRecord(value.inventory.nginx) || !isRecord(value.inventory.docker)) return false;
  if (!isRecord(value.inventory.database)) return false;
  if (typeof value.updatedAt !== "string") return false;
  return true;
};

export const isSystemUsersSummaryResponse = (value: unknown): value is SystemUsersSummaryResponse => {
  if (!isRecord(value) || value.ok !== true) return false;
  if (!isRecord(value.summary)) return false;
  if (typeof value.updatedAt !== "string") return false;
  return true;
};

export const isSystemUpdatesResponse = (value: unknown): value is SystemUpdatesResponse => {
  if (!isRecord(value) || value.ok !== true) return false;
  if (typeof value.updatedAt !== "string") return false;
  if (!isNumber(value.count)) return false;
  if (!Array.isArray(value.items)) return false;
  return true;
};

export const isSystemRedisStatusResponse = (value: unknown): value is SystemRedisStatusResponse => {
  if (!isRecord(value) || value.ok !== true) return false;
  if (!isRecord(value.status)) return false;
  if (typeof value.status.configured !== "boolean") return false;
  if (typeof value.status.reachable !== "boolean") return false;
  if (typeof value.status.serviceActive !== "boolean") return false;
  if (value.status.version !== null && typeof value.status.version !== "string") return false;
  return true;
};
// Formatters
/** Locale is explicit so callers that copy diagnostics keep the original bytes. */
export const formatFixed = (value: number, digits: number, locale?: string): string => locale
  ? new Intl.NumberFormat(locale, { minimumFractionDigits: digits, maximumFractionDigits: digits, useGrouping: false }).format(Number(value.toFixed(digits)))
  : value.toFixed(digits);

export const formatBytes = (bytes?: number | null, locale?: string): string => {
  if (!Number.isFinite(bytes ?? NaN)) return "—";
  const units = ["B", "KB", "MB", "GB", "TB", "PB"] as const;
  let value = Number(bytes);
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  const precision = value >= 10 ? 0 : 1;
  return `${formatFixed(value, precision, locale)} ${units[unitIndex]}`;
};

/**
 * Split a byte count into its numeric value and unit so a consumer can style
 * the magnitude and the unit suffix independently (e.g. a large `tabular-nums`
 * value with a demoted unit). Mirrors `formatBytes` scaling exactly.
 *
 * Returns `{ value: "—", unit: "" }` for non-finite input so callers can render
 * the same em-dash placeholder `formatBytes` produces.
 */
export const formatBytesParts = (bytes?: number | null): { value: string; unit: string } => {
  if (!Number.isFinite(bytes ?? NaN)) return { value: "—", unit: "" };
  const units = ["B", "KB", "MB", "GB", "TB", "PB"] as const;
  let value = Number(bytes);
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  const precision = value >= 10 ? 0 : 1;
  return { value: value.toFixed(precision), unit: units[unitIndex] };
};

export const formatRate = (bytes?: number | null): string =>
  Number.isFinite(bytes ?? NaN) ? `${formatBytes(bytes)}/s` : "—";

export const formatPercent = (value?: number | null, locale?: string): string =>
  Number.isFinite(value ?? NaN) ? `${formatFixed(Number(value), 1, locale)}%` : "—";

export const formatRelative = (iso?: string | null): string => {
  if (!iso) return "";
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return "";
  const diffSeconds = Math.max(0, Math.round((Date.now() - ts) / 1000));
  if (diffSeconds < 60) return `${diffSeconds}s ago`;
  if (diffSeconds < 3600) return `${Math.floor(diffSeconds / 60)}m ago`;
  if (diffSeconds < 86400) return `${Math.floor(diffSeconds / 3600)}h ago`;
  return `${Math.floor(diffSeconds / 86400)}d ago`;
};

export const formatNumber = (value: number): string => {
  if (!Number.isFinite(value)) return "0";
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
  return value.toLocaleString();
};

// Status resolvers
export type KpiState = "loading" | "unavailable" | "unknown" | "stale" | "live";
export const resolveKpiState = (
  snapshot: TelemetrySnapshot | null,
  loading: boolean,
  error: string | null,
  now = Date.now()
): KpiState => {
  if (loading) return "loading";
  if (error) return "unavailable";
  if (!snapshot) return "unknown";
  const ageSeconds = Math.max(0, Math.round((now - Date.parse(snapshot.sampledAt)) / 1000));
  return ageSeconds > 30 ? "stale" : "live";
};

const KPI_PRESENTATION = {
  loading: { label: "Loading", variant: "muted" },
  unavailable: { label: "Unavailable", variant: "danger" },
  unknown: { label: "Unknown", variant: "warning" },
  stale: { label: "Stale", variant: "warning" },
  live: { label: "Live", variant: "success" },
} as const;

export const kpiStatePresentation = (state: KpiState): MetricStatus => ({ ...KPI_PRESENTATION[state] });

export const resolveKpiStatus = (
  snapshot: TelemetrySnapshot | null,
  loading: boolean,
  error: string | null
): MetricStatus => kpiStatePresentation(resolveKpiState(snapshot, loading, error));

export const resolveHealthStatus = (percent: number | null): MetricStatus => {
  if (percent === null) return { label: "Unknown", variant: "muted" };
  if (percent < 60) return { label: "Healthy", variant: "success" };
  if (percent < 80) return { label: "Warning", variant: "warning" };
  return { label: "Critical", variant: "danger" };
};

export const resolveVnstatStatus = (
  data: VnstatResponse | null,
  loading: boolean,
  error: string | null
): MetricStatus => {
  if (loading) return { label: "Loading", code: "loading", variant: "muted" };
  if (error) return { label: "Issue", code: "issue", variant: "danger" };
  if (!data) return { label: "No data", code: "noData", variant: "warning" };
  return { label: "Healthy", code: "healthy", variant: "success" };
};

// Health score calculation
export type HealthScoreBreakdown = {
  cpu: number;
  memory: number;
  disk: number;
};

export type HealthScoreResult = {
  score: number;
  breakdown: HealthScoreBreakdown;
  status: MetricStatus;
};

/**
 * Calculate composite system health score (0-100).
 * Weights: CPU 35%, Memory 35%, Disk 30%
 * Score = 100 - weighted usage (higher is healthier)
 */
export const calculateHealthScore = (snapshot: TelemetrySnapshot | null): HealthScoreResult | null => {
  if (!snapshot) return null;

  const cpuUsage = snapshot.cpu.percent ?? 0;
  const memUsage = snapshot.memory.percent;
  const diskUsage = snapshot.disk.percent ?? 0;

  // Individual scores (100 = best, 0 = worst)
  const cpuScore = Math.max(0, 100 - cpuUsage);
  const memScore = Math.max(0, 100 - memUsage);
  const diskScore = Math.max(0, 100 - diskUsage);

  // Weighted composite score
  const score = Math.round(cpuScore * 0.35 + memScore * 0.35 + diskScore * 0.30);

  // Determine status based on score
  let status: MetricStatus;
  if (score >= 70) {
    status = { label: "Healthy", code: "healthy", variant: "success" };
  } else if (score >= 40) {
    status = { label: "Warning", code: "warning", variant: "warning" };
  } else {
    status = { label: "Critical", code: "critical", variant: "danger" };
  }

  return {
    score,
    breakdown: {
      cpu: Math.round(cpuScore),
      memory: Math.round(memScore),
      disk: Math.round(diskScore),
    },
    status,
  };
};
